import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronUp, ChevronDown, Vote } from 'lucide-react';
import LoadingList from '@shared/ui/LoadingList';
import { useWorkspaceReports } from '../../hooks/useWorkspaceReports';
import api from '../../lib/api';
import { getLegislatorLinkClasses } from '../../utils/linkStyles';
import { getPartyBadgeColor, getPartyAbbreviation } from '../../utils/partyUtils';
import { formatDistrict, districtSortKey } from '../../utils/districtDisplay';
import { EMPTY_VALUE } from '../../utils/dateUtils';
import { getPositionLabel } from '../../utils/positionColors';
import {
  formatCompactScorecardPosition,
  getScorecardBillHeaderPosition,
  getScorecardScoreColorClass,
  getScorecardVoteTooltip,
} from '../../utils/scorecardDisplay';
import ReportLogoHeader from './ReportLogoHeader';
import { WorkspaceBadge } from '../WorkspaceBadge';
import ExportMenu from '../ExportMenu';
import EmptyState from '../EmptyState';
import { SortIcon } from '../icons';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableBody,
} from '../StandardTable';

interface Vote {
  vote: string | null;
  aligned: boolean;
  billNumber: string;
  billTitle: string;
  billPosition: string;
  isScoreable: boolean;
}

interface Legislator {
  legislatorId: number;
  legislatorName: string;
  chamber: string;
  district: string;
  party: string;
  workspace_name?: string;
  workspace_id?: string;
  yearScore: number | null;
  alignedVotes: number;
  totalScoredVotes: number;
  votes: { [billId: string]: Vote };
}

interface Bill {
  billId: number;
  billNumber: string;
  billTitle: string;
  billPosition: string;
  /** Org mode: per-workspace positions for this bill */
  workspaces?: { id?: string; name: string; position: string | null }[];
}

interface ScorecardData {
  legislators: Legislator[];
  bills: Bill[];
}

interface ScorecardReportProps {
  workspaceId?: string;
  workspaceName?: string;
  organizationId?: string;
  organizationName?: string;
  /** When set (public share), skip API fetch and render this payload */
  prefetchedData?: ScorecardData;
  /** Public share links + hide authenticated chrome (logo/export header) */
  isPublicView?: boolean;
}

type SortColumn = 'legislatorName' | 'district' | 'yearScore' | string;
type SortDirection = 'asc' | 'desc';

const ScorecardReport: React.FC<ScorecardReportProps> = ({
  workspaceId,
  workspaceName = 'Workspace',
  organizationId,
  organizationName,
  prefetchedData,
  isPublicView = false,
}) => {
  const hasPrefetched = !!prefetchedData;
  const isOrgMode = !!organizationId
    || !!(prefetchedData?.legislators?.some((l) => l.workspace_name));
  const displayName = isOrgMode ? (organizationName || 'Organization') : workspaceName;
  const [sortColumn, setSortColumn] = useState<SortColumn>('legislatorName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const currentYear = new Date().getFullYear();

  const { data: fetchedData, isLoading: loading, error: queryError } = useQuery(
    isOrgMode ? ['org-scorecard-report', organizationId] : ['scorecard-report', workspaceId],
    async () => {
      const url = isOrgMode
        ? `/org-reports/${organizationId}/scorecard`
        : `/workspace-reports/${workspaceId}/scorecard`;
      const response = await api.get(url);
      return response.data.data as ScorecardData;
    },
    {
      enabled: !hasPrefetched && (isOrgMode ? !!organizationId : !!workspaceId),
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    }
  );

  const { exportReport } = useWorkspaceReports(
    isPublicView ? undefined : workspaceId,
    isPublicView ? undefined : organizationId
  );

  const handleExport = async (format: 'excel' | 'csv' | 'pdf') => {
    try {
      await exportReport('scorecard', format, { viewMode: 'table' });
    } catch (error) {
      console.error('Error exporting scorecard:', error);
    }
  };

  const data = (hasPrefetched ? prefetchedData : fetchedData) as ScorecardData | undefined;
  const error = !hasPrefetched && queryError ? 'Failed to load scorecard data' : null;

  const getBillHref = (bill: Bill) => {
    if (isPublicView) {
      const billNumOnly = (bill.billNumber || '').split(':')[0].trim();
      return `/public/bills/${encodeURIComponent(billNumOnly)}`;
    }
    return isOrgMode
      ? `/legislation/${bill.billId}`
      : `/legislation/${bill.billId}?workspace=${workspaceId}`;
  };

  const getLegislatorHref = (legislator: Legislator) => {
    if (isPublicView) return `/public/legislators/${legislator.legislatorId}`;
    return isOrgMode
      ? `/legislators/${legislator.legislatorId}`
      : `/legislators/${legislator.legislatorId}?workspace=${workspaceId}`;
  };


  // Sort legislators based on selected column
  const sortedLegislators = useMemo(() => {
    if (!data?.legislators) return [];

    const sorted = [...data.legislators];
    
    sorted.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (sortColumn === 'legislatorName') {
        aValue = a.legislatorName.toLowerCase();
        bValue = b.legislatorName.toLowerCase();
      } else if (sortColumn === 'district') {
        // Canonical order: CO House → CO Senate → US House, numeric within.
        aValue = districtSortKey(a.district);
        bValue = districtSortKey(b.district);
      } else if (sortColumn === 'yearScore') {
        aValue = a.yearScore ?? -1;
        bValue = b.yearScore ?? -1;
      } else if (sortColumn.startsWith('bill-')) {
        // Sorting by a specific bill column
        const billId = sortColumn.replace('bill-', '');
        // Ensure we're using string keys to match the votes object
        const aVote = a.votes[String(billId)];
        const bVote = b.votes[String(billId)];
        
        // Sort by alignment: aligned first, then not aligned, then no vote
        if (aVote?.aligned && !bVote?.aligned) return -1;
        if (!aVote?.aligned && bVote?.aligned) return 1;
        if (aVote?.vote && !bVote?.vote) return -1;
        if (!aVote?.vote && bVote?.vote) return 1;
        return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      // Tiebreaker: always sort alphabetically by name
      return a.legislatorName.localeCompare(b.legislatorName);
    });

    return sorted;
  }, [data?.legislators, sortColumn, sortDirection]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection(column === 'yearScore' ? 'desc' : 'asc');
    }
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return <SortIcon className="w-4 h-4 text-white/60" />;
    }
    return sortDirection === 'asc' ?
      <ChevronUp className="w-4 h-4 text-white" /> :
      <ChevronDown className="w-4 h-4 text-white" />;
  };

  const getBillHeaderPosition = (bill: Bill): string | null => {
    const raw = getScorecardBillHeaderPosition(bill, isOrgMode);
    return raw ? getPositionLabel(raw) : null;
  };

  /**
   * Vote mark. In org mode, when this workspace tracks the bill, show that
   * workspace's position under the mark (header may omit position when workspaces disagree).
   */
  const renderVoteCell = (vote: Vote | undefined, options?: { showRowPosition?: boolean }) => {
    const showRowPosition = !!options?.showRowPosition && !!vote?.billPosition;
    const compactPosition = formatCompactScorecardPosition(vote?.billPosition);
    const tooltip = getScorecardVoteTooltip(vote);

    let mark: React.ReactNode;
    if (!vote || !vote.vote) {
      mark = <span className="text-text-tertiary dark:text-text-dark-tertiary">{EMPTY_VALUE}</span>;
    } else if (vote.aligned) {
      mark = <span className="text-success-600 dark:text-success-400 font-bold text-lg leading-none">✓</span>;
    } else {
      mark = <span className="text-error-600 dark:text-error-400 font-bold text-lg leading-none">✗</span>;
    }

    if (!showRowPosition || !compactPosition) {
      return <span title={tooltip}>{mark}</span>;
    }

    return (
      <div className="flex flex-col items-center gap-0.5" title={tooltip}>
        {mark}
        <span className="text-xs leading-none font-medium text-text-tertiary dark:text-text-dark-tertiary">
          {compactPosition}
        </span>
      </div>
    );
  };

  if (!hasPrefetched && loading) {
    return <LoadingList rows={6} label="Loading scorecard" />;
  }

  if (error) {
    return (
      <EmptyState
        icon={Vote}
        title="Couldn’t load scorecard"
        hint={error}
        tone="error"
      />
    );
  }

  if (!data?.legislators || data.legislators.length === 0) {
    return (
      <EmptyState
        icon={Vote}
        title="No scorecard data yet"
        hint="Track legislators and bills with a Support, Strong Support, Oppose, or Strong Oppose position to see voting alignment scores."
      />
    );
  }

  if (data.bills.length === 0) {
    return (
      <EmptyState
        icon={Vote}
        title="No scoreable bills"
        hint={
          <>
            Set a position of{' '}
            <strong className="text-text-primary dark:text-text-dark-primary">Support</strong>,{' '}
            <strong className="text-text-primary dark:text-text-dark-primary">Strong Support</strong>,{' '}
            <strong className="text-text-primary dark:text-text-dark-primary">Oppose</strong>, or{' '}
            <strong className="text-text-primary dark:text-text-dark-primary">Strong Oppose</strong>{' '}
            on a tracked bill in the Bill Tracking tab.
          </>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      {!isPublicView && (
        <>
          <ReportLogoHeader workspaceId={workspaceId} />
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-left text-text-primary dark:text-text-dark-primary">
                {displayName} - Scorecard Report
              </h3>
              <p className="text-sm text-left text-text-secondary dark:text-text-dark-secondary mt-1">
                Voting alignment scores based on final reading votes
              </p>
            </div>
            <div className="flex items-center gap-2">
              <ExportMenu
                formats={['excel', 'csv', 'pdf']}
                onExport={(format) => handleExport(format as 'csv' | 'excel' | 'pdf')}
              />
            </div>
          </div>
        </>
      )}

      {/* Scrollable matrix — StandardTable owns horizontal scroll (no overflow wrapper) */}
      <StandardTable variant="report" horizontalScroll stickyHeader>
        <StandardTableHeader>
            <tr>
              {/* Legislator Name Column - pinned left */}
              <th
                onClick={() => handleSort('legislatorName')}
                className="sticky left-0 z-20 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors border-r border-border-light dark:border-border-dark min-w-[200px]"
              >
                <div className="flex items-center gap-1">
                  Legislator
                  {getSortIcon('legislatorName')}
                </div>
              </th>

              {/* Workspace Column - only in org mode */}
              {isOrgMode && (
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Workspace
                </th>
              )}

              {/* District Column */}
              <th
                onClick={() => handleSort('district')}
                className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-1">
                  District
                  {getSortIcon('district')}
                </div>
              </th>

              {/* Year Score Column */}
              <th
                onClick={() => handleSort('yearScore')}
                className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center justify-center gap-1">
                  {currentYear} Score
                  {getSortIcon('yearScore')}
                </div>
              </th>

              {/* Dynamic Bill Columns */}
              {data.bills.map((bill: Bill) => {
                const headerPosition = getBillHeaderPosition(bill);
                const billHref = getBillHref(bill);
                return (
                <th
                  key={bill.billId}
                  onClick={() => handleSort(`bill-${bill.billId}`)}
                  className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors min-w-[100px] text-white"
                >
                  <div className="flex flex-col items-center gap-1">
                    <div className="flex items-center gap-1">
                      {isPublicView ? (
                        <a
                          href={billHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={bill.billTitle}
                          className="text-white font-semibold hover:underline cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {bill.billNumber}
                        </a>
                      ) : (
                        <Link
                          to={billHref}
                          title={bill.billTitle}
                          className="text-white font-semibold hover:underline cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {bill.billNumber}
                        </Link>
                      )}
                      {getSortIcon(`bill-${bill.billId}`)}
                    </div>
                    {headerPosition && (
                      <span className="text-xs font-normal normal-case text-white/80 font-semibold">
                        ({headerPosition})
                      </span>
                    )}
                  </div>
                </th>
                );
              })}
            </tr>
        </StandardTableHeader>

        <StandardTableBody>
            {sortedLegislators.map((legislator) => {
              const legislatorHref = getLegislatorHref(legislator);
              return (
              <tr
                key={`${legislator.legislatorId}-${legislator.workspace_id || legislator.workspace_name || 'ws'}`}
                className="hover:bg-background-tertiary dark:hover:bg-background-dark-secondary transition-colors"
              >
                {/* Legislator Name - pinned left */}
                <td className="sticky left-0 z-10 bg-background dark:bg-background-dark-primary px-4 py-3 whitespace-nowrap text-sm font-medium border-r border-border-light dark:border-border-dark">
                  <div className="flex items-center gap-1.5">
                    {isPublicView ? (
                      <a
                        href={legislatorHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={getLegislatorLinkClasses(legislator.party)}
                      >
                        {legislator.legislatorName} ({legislator.chamber})
                      </a>
                    ) : (
                      <Link
                        to={legislatorHref}
                        className={getLegislatorLinkClasses(legislator.party)}
                      >
                        {legislator.legislatorName} ({legislator.chamber})
                      </Link>
                    )}
                    <span className={`inline-flex items-center px-1 py-0.5 rounded text-xs font-semibold ${getPartyBadgeColor(legislator.party)}`}>
                      {getPartyAbbreviation(legislator.party) || 'U'}
                    </span>
                  </div>
                </td>

                {/* Workspace - only in org mode */}
                {isOrgMode && (
                  <td className="px-4 py-3 text-sm">
                    {legislator.workspace_name && (
                      <WorkspaceBadge workspace={{ name: legislator.workspace_name }} />
                    )}
                  </td>
                )}

                {/* District */}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-text-secondary dark:text-text-dark-secondary">
                  {formatDistrict(null, legislator.district, legislator.chamber) || EMPTY_VALUE}
                </td>

                {/* Year Score */}
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <div className="flex flex-col items-center">
                    <div className={`text-lg font-bold ${getScorecardScoreColorClass(legislator.yearScore)}`}>
                      {legislator.yearScore !== null ? `${legislator.yearScore}%` : EMPTY_VALUE}
                    </div>
                    {legislator.yearScore !== null && (
                      <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                        {legislator.alignedVotes}/{legislator.totalScoredVotes}
                      </div>
                    )}
                  </div>
                </td>

                {/* Dynamic Bill Vote Columns */}
                {data.bills.map((bill: Bill) => {
                  const voteData = legislator.votes[String(bill.billId)];
                  return (
                    <td
                      key={bill.billId}
                      className="px-3 py-3 whitespace-nowrap text-center text-sm"
                    >
                      {renderVoteCell(voteData, { showRowPosition: isOrgMode })}
                    </td>
                  );
                })}
              </tr>
              );
            })}
        </StandardTableBody>
      </StandardTable>

      {/* Legend */}
      <div className="card p-4">
        <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary mb-2">Legend</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-success-600 dark:text-success-400 font-bold text-lg">✓</span>
            <span className="text-text-secondary dark:text-text-dark-secondary">Vote aligned with position</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-error-600 dark:text-error-400 font-bold text-lg">✗</span>
            <span className="text-text-secondary dark:text-text-dark-secondary">Vote not aligned with position</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-text-tertiary dark:text-text-dark-tertiary">{EMPTY_VALUE}</span>
            <span className="text-text-secondary dark:text-text-dark-secondary">
              {isOrgMode
                ? 'No final reading vote, or bill not scored in this workspace'
                : 'No final reading vote recorded'}
            </span>
          </div>
          {isOrgMode && (
            <div className="flex items-center gap-2 col-span-2">
              <span className="text-text-secondary dark:text-text-dark-secondary text-xs">
                In org view, the label under each mark (<strong>Sup</strong> / <strong>Sup+</strong> / <strong>Opp</strong> / <strong>Opp+</strong>) is
                that workspace&apos;s position. Column headers only show a position when every workspace agrees.
              </span>
            </div>
          )}
          <div className="flex items-center gap-2 col-span-2">
            <span className="text-text-secondary dark:text-text-dark-secondary text-xs">
              Only bills with a <strong>Support</strong>, <strong>Strong Support</strong>, <strong>Oppose</strong>, or <strong>Strong Oppose</strong> position appear in this report.
              To include a tracked bill, update its position in the Bill Tracking tab.
            </span>
          </div>
          <div className="flex items-center gap-2 col-span-2">
            <span className="text-text-secondary dark:text-text-dark-secondary text-xs">
              Score only includes bills where a final reading vote was recorded (bills showing &quot;{EMPTY_VALUE}&quot; are excluded from the denominator)
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScorecardReport;

